import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/ui/chip";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { BookA, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type { GlossaryEntry } from "@iamu/shared";
import { GlossaryTermDialog } from "./context-entry-dialogs";

/**
 * Format glossary entries into a DOMAIN VOCABULARY prompt block.
 * Mirrors packages/ai/src/glossary-prompt.ts for client-side preview.
 */
export function formatGlossaryForPrompt(
  glossary: GlossaryEntry[] | null | undefined,
): string {
  if (!glossary || glossary.length === 0) return "";

  const lines = glossary.map((entry) => {
    const parts: string[] = [];

    if (entry.translation) {
      parts.push(`- ${entry.canonicalTerm} (${entry.translation})`);
    } else {
      parts.push(`- ${entry.canonicalTerm}`);
    }

    if (entry.definition) {
      parts.push(`: ${entry.definition}`);
    }

    if (entry.variants.length > 0) {
      const notItems = entry.variants.map((v) => `"${v}"`).join(", ");
      parts.push(`. NOT ${notItems}`);
    }

    return parts.join("");
  });

  return `DOMAIN VOCABULARY (use these terms consistently):\n${lines.join("\n")}`;
}

interface GlossarySectionProps {
  projectId: string;
  glossary: GlossaryEntry[];
  canEdit: boolean;
  onGlossaryChange?: (updated: GlossaryEntry[]) => void;
}

export function GlossarySection({
  projectId,
  glossary,
  canEdit,
  onGlossaryChange,
}: GlossarySectionProps) {
  const utils = trpc.useUtils();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const updateMutation = trpc.terminology.updateGlossary.useMutation({
    onSuccess: (updated) => {
      if (Array.isArray(updated)) {
        utils.terminology.getGlossary.setData({ projectId }, updated);
        onGlossaryChange?.(updated);
      }
      toast.success("Domain glossary updated");
    },
    onError: (err) => toastTrpcError(err, "Failed to update domain glossary"),
  });

  function persistGlossary(updated: GlossaryEntry[]) {
    onGlossaryChange?.(updated);
    utils.terminology.getGlossary.setData({ projectId }, updated);
    updateMutation.mutate({ projectId, glossary: updated });
  }

  function handleSaveEntry(draft: {
    canonicalTerm: string;
    translation?: string;
    definition?: string;
    variants: string[];
  }) {
    let updated: GlossaryEntry[];
    if (editingIndex !== null && glossary[editingIndex]) {
      const existing = glossary[editingIndex];
      const nextEntry: GlossaryEntry = {
        ...existing,
        canonicalTerm: draft.canonicalTerm,
        translation: draft.translation || undefined,
        definition: draft.definition || undefined,
        variants: draft.variants,
      };
      updated = glossary.map((item, idx) =>
        idx === editingIndex ? nextEntry : item,
      );
    } else {
      const newEntry: GlossaryEntry = {
        id: crypto.randomUUID(),
        canonicalTerm: draft.canonicalTerm,
        translation: draft.translation || undefined,
        definition: draft.definition || undefined,
        variants: draft.variants,
        sourceRefs: [],
        addedBy: "user",
        addedAt: new Date().toISOString(),
      };
      updated = [...glossary, newEntry];
    }

    persistGlossary(updated);
    setIsDialogOpen(false);
    setEditingIndex(null);
  }

  function removeEntry(index: number) {
    const updated = glossary.filter((_, idx) => idx !== index);
    persistGlossary(updated);
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <BookA className="h-4 w-4 text-primary" />
            <CardTitle className="text-base">Domain glossary</CardTitle>
            {glossary.length > 0 && (
              <Chip tone="neutral" className="text-xs tabular-nums">
                {glossary.length}
              </Chip>
            )}
          </div>
          <CardDescription>
            Canonical domain terms, translations, definitions, and rejected
            synonyms injected into walkthrough runners to prevent vocabulary
            drift.
          </CardDescription>
        </div>
        {canEdit && (
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => {
              setEditingIndex(null);
              setIsDialogOpen(true);
            }}
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add term
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {glossary.length === 0 ? (
          <div className="py-6 text-center space-y-2">
            <p className="text-sm text-muted-foreground">
              No domain glossary terms defined yet.
            </p>
            <p className="text-xs text-muted-foreground max-w-md mx-auto">
              Define canonical terms, definitions, and disallowed synonyms so
              walkthrough runners and scenario generators use your exact domain
              vocabulary.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border/60">
            {glossary.map((entry, i) => (
              <div
                key={entry.id}
                className="flex items-start justify-between gap-3 py-3"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="text-sm font-medium text-foreground">
                      {entry.canonicalTerm}
                    </span>
                    {entry.translation && (
                      <span className="text-xs text-muted-foreground">
                        ({entry.translation})
                      </span>
                    )}
                  </div>
                  {entry.definition && (
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {entry.definition}
                    </p>
                  )}
                  {entry.variants.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                      <span className="text-xs text-muted-foreground">
                        Avoid:
                      </span>
                      {entry.variants.map((variant) => (
                        <Chip key={variant} tone="outline" className="text-xs">
                          {variant}
                        </Chip>
                      ))}
                    </div>
                  )}
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setEditingIndex(i);
                        setIsDialogOpen(true);
                      }}
                      aria-label={`Edit term ${entry.canonicalTerm}`}
                    >
                      <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                    <SimpleTooltip
                      content={`Remove term ${entry.canonicalTerm}`}
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeEntry(i)}
                        aria-label={`Remove term ${entry.canonicalTerm}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                    </SimpleTooltip>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {canEdit && isDialogOpen && (
        <GlossaryTermDialog
          initialEntry={
            editingIndex !== null ? glossary[editingIndex] : undefined
          }
          onSave={handleSaveEntry}
          onClose={() => {
            setIsDialogOpen(false);
            setEditingIndex(null);
          }}
        />
      )}
    </Card>
  );
}
